import React, { useState } from "react";
import {
  Card,
  Form,
  Button,
  InputGroup,
  ProgressBar,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import apiClient from "utils/apiClient";
import { toast } from "react-toastify";

export default function PasswordForm({ onBack }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Simple password strength check
  const getPasswordStrength = () => {
    if (newPassword.length >= 12) return 100;
    if (newPassword.length >= 8) return 70;
    if (newPassword.length >= 5) return 40;
    return 20;
  };

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields.");
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.put(
        "/identity-service/api/v1/auth/password",
        {
          currentPassword,
          newPassword,
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success("Password changed successfully!");
        resetForm();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || "Failed to change password");
      }
    } catch (err) {
      if (err.response) {
        console.error("Server error:", err.response.data);
        toast.error(err.response.data.message || "Login failed");
      } else if (err.request) {
        console.error("No response received:", err.request);
        toast.error("No response from server, please try again later");
      } else {
        console.error("Error setting up request:", err.message);
        toast.error("Login error: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row className="justify-content-centerdd">
      <Col xs={12} lg={12}>
        <Button
          variant="light"
          size="sm"
          className="border text-xxs mb-3"
          onClick={onBack}
        >
          <FontAwesomeIcon
            icon="chevron-left"
            className="me-2 text-secondary"
          />
          Back
        </Button>
        <h6 className="mb-0 fw-semibold text-sm">Change Password</h6>
        <p className="mb-3 text-xxs">
          Create a new password for your account. New password must be different
          from any previous passwords.
        </p>

        <Form onSubmit={handleSubmit}>
          {/* Current Password */}
          <Form.Group className="mb-3">
            <Form.Label className="text-xxs fw-semibold">
              Current Password
            </Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Form.Group>

          {/* New Password */}
          <Form.Group className="mb-3">
            <Form.Label className="text-xxs fw-semibold">
              New Password
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Button
                variant="light border"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
            {newPassword && (
              <ProgressBar
                now={getPasswordStrength()}
                variant={
                  getPasswordStrength() < 50
                    ? "danger"
                    : getPasswordStrength() < 80
                    ? "warning"
                    : "success"
                }
                className="mt-2"
                style={{ height: "6px" }}
              />
            )}
            <small className="text-muted">
              Use at least 8 characters, mix of letters, numbers & symbols.
            </small>
          </Form.Group>

          {/* Confirm Password */}
          <Form.Group className="mb-4">
            <Form.Label className="text-xxs fw-semibold">
              Confirm New Password
            </Form.Label>
            <Form.Control
              type={showPassword ? "text" : "password"}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isInvalid={confirmPassword && newPassword !== confirmPassword}
            />
          </Form.Group>

          {loading ? (
            <Spinner size="sm" variant="success" />
          ) : (
            <Button
              type="submit"
              variant="success"
              className="text-sm px-4 inline-block"
            >
              Update Password
            </Button>
          )}
        </Form>
      </Col>
    </Row>
  );
}
